import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { selectIsSMSLinkShorteningFeatureFlagOn } from 'core/userContext/selectors';
import { ITERABLE_LINKS_HOST } from 'constants/urls';
import { FlexSpacer } from 'ui/flexSpacer';
import { BoldText, H3 } from 'ui/theme/typography/components';
import { AddNew16 } from 'ui/icons/aurora/16px';
import { Spinner } from 'ui/spinner';
import { PrimaryButton, BUTTON_VARIANTS } from 'ui/button';
import { ConfirmModal } from 'ui/modal';
import { Table } from 'ui/table';
import { sortRows } from 'ui/table/utils';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { DESCENDING } from 'constants/sortDirections';
import { getIsLoading } from '../selectors';
import { setDefaultTrackingDomainRequest, deleteTrackingDomainRequest } from '../actions';
import { configureTrackingDomainsTableColumns } from './TrackingDomainsTableColumns';
import { CreateTrackingDomainModal } from './CreateTrackingDomainModal';
import { EmptyStateContainer } from './MailDomains.styled';
import {
  CREATE_TRACKING_DOMAIN,
  DELETE_TRACKING_DOMAIN,
  SET_DEFAULT_TRACKING_DOMAIN,
} from '../constants';
// import { TrackingDomain } from '../types';

// type Props = {
//   trackingDomains: Array<TrackingDomain>,
// };

export const TrackingDomainsTable = ({ trackingDomains = [] }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLinkShorteningFeatureFlagOn = useSelector(selectIsSMSLinkShorteningFeatureFlagOn);

  const [currentSortDirection, setSortDirection] = useState(DESCENDING);
  const [sortedBy, setSortedBy] = useState('domain');
  const [modalState, setModalState] = useState('');
  const [selectedTrackingDomain, setSelectedTrackingDomain] = useState();

  const updateTrackingDomain = useCallback(() => {
    setModalState('');
    dispatch(
      setDefaultTrackingDomainRequest({
        ...selectedTrackingDomain,
        isDefault: true,
      }),
    );
  }, [dispatch, selectedTrackingDomain]);

  const deleteTrackingDomain = useCallback(() => {
    setModalState('');
    dispatch(deleteTrackingDomainRequest(selectedTrackingDomain.domain));
  }, [dispatch, selectedTrackingDomain]);

  const sortedRows = sortRows(trackingDomains, sortedBy, currentSortDirection);
  const onOpen = (domain, modalType) => {
    setSelectedTrackingDomain(domain);
    setModalState(modalType);
  };
  const onClose = () => setModalState('');

  const trackingDomainsTableColumns = configureTrackingDomainsTableColumns({
    onEdit: ({ domain }) => history.push(`/settings/domains/${encodeURIComponent(domain)}`),
    onSetDefault: (domain) => onOpen(domain, SET_DEFAULT_TRACKING_DOMAIN),
    onDelete: (domain) => onOpen(domain, DELETE_TRACKING_DOMAIN),
  });

  const createTrackingDomainModalIsOpen = modalState === CREATE_TRACKING_DOMAIN;
  const deleteTrackingDomainModalIsOpen = modalState === DELETE_TRACKING_DOMAIN;
  const setDefaultTrackingDomainModalIsOpen = modalState === SET_DEFAULT_TRACKING_DOMAIN;

  const isLoading = useSelector(getIsLoading);
  return (
    <>
      {isLoading ? (
        <EmptyStateContainer data-test="emptyState">
          <Spinner />
        </EmptyStateContainer>
      ) : (
        <>
          <FlexSpacer flexDirection="column" alignItems="stretch">
            <FlexSpacer justifyContent="space-between">
              <H3>Tracking domains</H3>
              <PrimaryButton
                data-test="mailDomains-create-tracking-domain-button"
                onClick={() => setModalState(CREATE_TRACKING_DOMAIN)}
              >
                <AddNew16 />
                New tracking domain
              </PrimaryButton>
            </FlexSpacer>
            <div>
              {isLinkShorteningFeatureFlagOn ? (
                <p>
                  For emails and SMS messages, tracking domains register clicks and then redirect
                  users to their intended destinations. The default is {ITERABLE_LINKS_HOST}, but
                  it's best to use a custom URL that matches your brand. For shorter links, use
                  shorter tracking domains.
                </p>
              ) : (
                <p>
                  Before sending an email campaign, Iterable rewrites links to point to a tracking
                  domain, which collects click metrics before redirecting to the original link
                  destination. By default, links are rewritten to {ITERABLE_LINKS_HOST}, but you can
                  create custom tracking domains that match your brand.
                </p>
              )}
            </div>
            <Table data-test="tracking-domains-table">
              <TableHeader
                columns={trackingDomainsTableColumns}
                setSortedBy={setSortedBy}
                setSortDirection={setSortDirection}
                sortDirection={currentSortDirection}
                sortedBy={sortedBy}
              />
              <TableRows idKey="domain" columns={trackingDomainsTableColumns} rows={sortedRows} />
            </Table>
            {!sortedRows.length && (
              <FlexSpacer padding="cardPadding" justifyContent="center">
                No results
              </FlexSpacer>
            )}
          </FlexSpacer>
        </>
      )}
      {createTrackingDomainModalIsOpen && (
        <CreateTrackingDomainModal onClose={onClose} trackingDomains={trackingDomains} />
      )}
      {deleteTrackingDomainModalIsOpen && (
        <ConfirmModal
          cancelLabel="Cancel"
          confirmButtonContent="Delete"
          confirmButtonVariant={BUTTON_VARIANTS.DESTRUCTIVE}
          headerContent="Delete Tracking Domain"
          id="delete-tracking-domain-modal"
          isModalOpen={deleteTrackingDomainModalIsOpen}
          onClose={onClose}
          onConfirm={deleteTrackingDomain}
          bodyContent={
            <>
              Are you sure you would like to delete{' '}
              <BoldText>{selectedTrackingDomain.domain}</BoldText>?
            </>
          }
        />
      )}
      {setDefaultTrackingDomainModalIsOpen && (
        <ConfirmModal
          cancelLabel="Cancel"
          confirmButtonContent="Set as default"
          headerContent="Set As Default Tracking Domain"
          id="set-default-tracking-domain-modal"
          isModalOpen={setDefaultTrackingDomainModalIsOpen}
          onClose={onClose}
          onConfirm={updateTrackingDomain}
          bodyContent={
            <>
              Are you sure you would like to set{' '}
              <BoldText>{selectedTrackingDomain.domain}</BoldText> as the default domain?
            </>
          }
        />
      )}
    </>
  );
};
